import { useState } from 'react'
import { icons } from '../../styles/images'
import GTButton from '../atoms/buttons/GTButton'
import { useOptionalGoalCreation } from '../goals/creation/shared/GoalCreationContext'
import { activeGoals } from '../goals/goalTypes'
import GTDropdownMenu from '../radix/GTDropdownMenu'
import { GTMenuItem } from '../radix/RadixUIConstants'

interface GoalSelectorProps {
    value?: string | null
    onChange: (goalId: string | null) => void
    disabled?: boolean
    description?: string
}

/**
 * The "Goal" property: a task belongs to at most one goal (Linear's issue → project model).
 * Unset it is a bare target icon like the other property buttons; set, it reads the goal title.
 */
const GoalSelector = ({ value, onChange, disabled, description }: GoalSelectorProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const goals = useOptionalGoalCreation()
    if (!goals) return null
    // Paused goals stay visible so a linked task still reads its goal, but can't take new work.
    // Archived goals are not offered at all — an archived goal takes no new work of any kind, and the
    // server refuses the link. `selected` still looks at every goal so a task that is somehow still
    // pointing at one keeps reading its title instead of going blank; "No goal" is the way out.
    const selected = goals.allGoals.find((g) => g.id === value)

    const items: GTMenuItem[][] = [
        activeGoals(goals.allGoals).map((g) => ({
            label: g.status === 'paused' ? `${g.title} (paused)` : g.title,
            icon: icons.check_circle_wavy,
            selected: g.id === value,
            disabled: g.status === 'paused' && g.id !== value,
            onClick: () => onChange(g.id),
        })),
        [
            {
                label: 'No goal',
                icon: icons.x,
                selected: !value,
                onClick: () => onChange(null),
            },
        ],
    ]

    return (
        <GTDropdownMenu
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            menuInModal
            disabled={disabled || goals.isLinkingTaskToGoal}
            items={items}
            itemLayout="singleIcon"
            description={description}
            trigger={
                selected ? (
                    <GTButton
                        styleType="control"
                        icon={icons.check_circle_wavy}
                        value={selected.title}
                        aria-label={`Goal: ${selected.title}`}
                        title="Goal"
                    />
                ) : (
                    <GTButton styleType="icon" icon={icons.check_circle_wavy} aria-label="Goal" title="Goal" />
                )
            }
        />
    )
}

export default GoalSelector
